import {
  ActivityStatusDto,
  PmcScoreDto,
  TeamEmployeeRiskLevelDto,
  TeamPlanStatusDto,
  TeamPlanStatusFilterDto,
} from '@/api/generated';

export type AreaTranslation = {
  alerts: {
    count_few?: string;
    count_many?: string;
    count_one: string;
    count_other?: string;
    empty: string;
    items: {
      absenceReturns: AreaAlertText;
      birthdays: AreaAlertText;
      endingContracts: AreaAlertText;
      headcount: AreaAlertText;
      overdueSteps: AreaAlertText;
      plansToApprove: AreaAlertText;
      withoutPlan: AreaAlertText;
    };
    title: string;
  };
  hrChanges: {
    absenceReturnsScope_few?: string;
    absenceReturnsScope_many?: string;
    absenceReturnsScope_one: string;
    absenceReturnsScope_other?: string;
    chartTitle: string;
    endingContractsScope_few?: string;
    endingContractsScope_many?: string;
    endingContractsScope_one: string;
    endingContractsScope_other?: string;
    kpi: {
      absenceReturns: string;
      endingContracts: string;
      headcount: string;
      headcountDelta: string;
      nearestInDays_few?: string;
      nearestInDays_many?: string;
      nearestInDays_one: string;
      nearestInDays_other?: string;
      nearestToday: string;
    };
    subtitle: string;
    title: string;
    widgets: {
      absenceReturns: string;
      endingContracts: string;
    };
  };
  hrEvents: {
    back: string;
    chartEmpty: string;
    chartHint: string;
    chartTooltipUnit_few?: string;
    chartTooltipUnit_many?: string;
    chartTooltipUnit_one: string;
    chartTooltipUnit_other?: string;
    dayFilter: string;
    filters: {
      clear_few?: string;
      clear_many?: string;
      clear_one: string;
      clear_other?: string;
      departments: string;
      regions: string;
      searchPlaceholder: string;
      selectedCount_few?: string;
      selectedCount_many?: string;
      selectedCount_one: string;
      selectedCount_other?: string;
    };
    pager: {
      next: string;
      position: string;
      previous: string;
      total_few?: string;
      total_many?: string;
      total_one: string;
      total_other?: string;
    };
    pages: Record<HrEventPageTextKey, { overviewLabel: string; title: string }>;
    relative: {
      inDays_few?: string;
      inDays_many?: string;
      inDays_one: string;
      inDays_other?: string;
      today: string;
    };
    subtitle: string;
    table: {
      department: string;
      empty: string;
      eventDate: string;
      externalEmployeeId: string;
      person: string;
      position: string;
      region: string;
    };
    widget: {
      empty: string;
      seeAll: string;
      seeAllWithMore_few?: string;
      seeAllWithMore_many?: string;
      seeAllWithMore_one: string;
      seeAllWithMore_other?: string;
    };
  };
  kpi: {
    employees: string;
    inDevelopmentPrograms: string;
    managers: string;
  };
  noScope: {
    description: string;
    title: string;
  };
  people: {
    activities: {
      empty: string;
      status: Record<ActivityStatusDto, string>;
      title: string;
    };
    badges: {
      activities: string;
      addPanel: string;
      afterPanel: string;
      contractEnding: string;
      contractEndingWithDate: string;
      longAbsence: string;
      noActivities: string;
      noPanel: string;
      panels: string;
    };
    count: string;
    empty: string;
    filters: {
      departmentsAll: string;
      departmentsLabel: string;
      planStatus: Record<AreaPlanStatusFilterKey, string>;
      planStatusAll: string;
      planStatusLabel: string;
      pmcAll: string;
      pmcLabel: string;
      programsAll: string;
      programsLabel: string;
      programsOnly: string;
      regionsAll: string;
      regionsLabel: string;
      risk: Record<'all' | TeamEmployeeRiskLevelDto, string>;
      riskAll: string;
      riskLabel: string;
      score: Record<PmcScoreDto, string>;
      sopAll: string;
      sopLabel: string;
      trainingsAll: string;
      trainingsLabel: string;
    };
    headers: {
      department: string;
      employee: string;
      plan: string;
      pmc: string;
      risk: string;
      roleTenure: string;
      sop: string;
      status: string;
    };
    loadMore: string;
    openProfile: string;
    planStatus: Record<TeamPlanStatusDto, string>;
    programsCount: string;
    roleTenureValue_few?: string;
    roleTenureValue_many?: string;
    roleTenureValue_one: string;
    roleTenureValue_other?: string;
    searchPlaceholder: string;
    showingCount: string;
  };
  pipeline: {
    buckets: {
      from1To2Years: string;
      from3To5Years: string;
      readyNow: string;
      upTo1Year: string;
    };
    title: string;
    total: string;
    totalValue: string;
  };
  pmc: {
    average: string;
    empty: string;
    scores: Record<PmcScoreDto, string>;
    title: string;
  };
  risk: {
    chips: {
      risk: Record<TeamEmployeeRiskLevelDto, string>;
    };
    empty: string;
    error: string;
    loadMore: string;
    peopleCount: string;
    riskTitle: string;
    showingCount: string;
  };
  stats: {
    adminView: string;
    backToStart: string;
    everyoneView: string;
    sectionTitle: string;
  };
  tabs: {
    dashboard: string;
    hrChanges: string;
    matrix: string;
    people: string;
    peopleWithCount: string;
    risk: string;
  };
};

type AreaAlertText = {
  subtitle: string;
  title: string;
};

type AreaPlanStatusFilterKey =
  | typeof TeamPlanStatusFilterDto.All
  | typeof TeamPlanStatusFilterDto.Approved
  | typeof TeamPlanStatusFilterDto.None
  | typeof TeamPlanStatusFilterDto.Overdue
  | typeof TeamPlanStatusFilterDto.Waiting;

type HrEventPageTextKey = 'absenceReturns' | 'birthdays' | 'employment' | 'endingContracts';

export const areaTranslation: AreaTranslation = {
  alerts: {
    count_one: '{{count}} alert',
    count_other: '{{count}} alerts',
    empty: 'Nothing needs your attention in your area.',
    items: {
      absenceReturns: {
        subtitle: 'Returns from long-term absence — worth planning a return conversation',
        title: '{{value}} returns from absence',
      },
      birthdays: {
        subtitle: 'Upcoming birthdays in your area',
        title: '{{value}} birthdays',
      },
      endingContracts: {
        subtitle: 'Contracts ending within the next 30 days',
        title: '{{value}} ending contracts',
      },
      headcount: {
        subtitle: 'Change of {{delta}} compared to 2 weeks ago',
        title: 'Headcount: {{value}}',
      },
      overdueSteps: {
        subtitle: 'Step deadline has passed and the step is not completed in the active cycle',
        title: '{{value}} employees with overdue steps',
      },
      plansToApprove: {
        subtitle: 'Plans ready for realization, waiting for supervisor approval',
        title: '{{value}} plans to approve',
      },
      withoutPlan: {
        subtitle: 'No development plan in the current cycle',
        title: '{{value}} employees without a development plan',
      },
    },
    title: 'Requires HRBP attention',
  },
  hrChanges: {
    absenceReturnsScope_one: '{{count}} person / 14 days',
    absenceReturnsScope_other: '{{count}} people / 14 days',
    chartTitle: 'Headcount — last 2 weeks',
    endingContractsScope_one: '{{count}} person / 30 days',
    endingContractsScope_other: '{{count}} people / 30 days',
    kpi: {
      absenceReturns: 'Returns from absence (3+ weeks)',
      endingContracts: 'Ending contracts (30 days)',
      headcount: 'Current headcount',
      headcountDelta: '{{delta}} vs 2 weeks ago ({{previous}})',
      nearestInDays_one: 'nearest in {{count}} day',
      nearestInDays_other: 'nearest in {{count}} days',
      nearestToday: 'nearest today',
    },
    subtitle:
      'As of {{date}} · headcount: 2 weeks back · contracts: 30 days ahead · returns: 14 days ahead',
    title: 'HR changes panel',
    widgets: {
      absenceReturns: 'Returns from long-term absence',
      endingContracts: 'Ending contracts',
    },
  },
  hrEvents: {
    back: 'Back',
    chartEmpty: 'No data for this period.',
    chartHint: 'Click a bar to filter the list by that day.',
    chartTooltipUnit_one: 'person',
    chartTooltipUnit_other: 'people',
    dayFilter: 'Day: {{date}}',
    filters: {
      clear_one: 'Clear ({{count}})',
      clear_other: 'Clear ({{count}})',
      departments: 'All departments',
      regions: 'All regions',
      searchPlaceholder: 'Search by name or SAP number',
      selectedCount_one: '{{count}} selected',
      selectedCount_other: '{{count}} selected',
    },
    pager: {
      next: 'Next',
      position: 'Page {{page}} of {{totalPages}}',
      previous: 'Previous',
      total_one: '{{count}} person',
      total_other: '{{count}} people',
    },
    pages: {
      absenceReturns: {
        overviewLabel: 'Daily',
        title: 'Returns from long-term absence',
      },
      birthdays: {
        overviewLabel: 'Daily',
        title: 'Team birthdays',
      },
      employment: {
        overviewLabel: 'Last 2 weeks',
        title: 'Headcount',
      },
      endingContracts: {
        overviewLabel: 'Contracts ending at month end',
        title: 'Ending contracts',
      },
    },
    relative: {
      inDays_one: 'in {{count}} day',
      inDays_other: 'in {{count}} days',
      today: 'today',
    },
    subtitle: 'Range {{from}} — {{to}}',
    table: {
      department: 'Department',
      empty: 'No people match these filters.',
      eventDate: 'Date',
      externalEmployeeId: 'SAP number',
      person: 'Employee',
      position: 'Position',
      region: 'Region',
    },
    widget: {
      empty: 'Nothing in this period.',
      seeAll: 'See all',
      seeAllWithMore_one: '+ {{count}} more — see all',
      seeAllWithMore_other: '+ {{count}} more — see all',
    },
  },
  kpi: {
    employees: 'Employees in area',
    inDevelopmentPrograms: 'In development programs',
    managers: 'Share of managers',
  },
  noScope: {
    description:
      'An HRBP area comes from HR data: the employees who have you listed as their HRBP. An HQ HRBP area is set by the access rules in the permissions panel. If someone is missing here, let the administrator know.',
    title: 'No area assigned to you',
  },
  people: {
    activities: {
      empty: 'No development activities recorded.',
      status: {
        [ActivityStatusDto.Completed]: 'Completed',
        [ActivityStatusDto.InProgress]: 'In progress',
        [ActivityStatusDto.Planned]: 'Planned',
      },
      title: 'Development activities',
    },
    badges: {
      activities: 'Development activities. Click to see the list',
      addPanel: 'No development panel recorded. Click to add one',
      afterPanel: 'After a development panel',
      contractEnding: 'Contract ending (within 30 days)',
      contractEndingWithDate: 'Contract ending on {{date}} (within 30 days)',
      longAbsence: 'Long-term absence',
      noActivities: 'No development activities',
      noPanel: 'No development panel recorded',
      panels: 'After a development panel. Click to see the history',
    },
    count: '{{shown}} of {{total}} employees',
    empty: 'No results',
    filters: {
      departmentsAll: 'All departments',
      departmentsLabel: 'Department',
      planStatus: {
        [TeamPlanStatusFilterDto.All]: 'All',
        [TeamPlanStatusFilterDto.Approved]: 'Approved',
        [TeamPlanStatusFilterDto.None]: 'No plan',
        [TeamPlanStatusFilterDto.Overdue]: 'Overdue',
        [TeamPlanStatusFilterDto.Waiting]: 'Waiting',
      },
      planStatusAll: 'Any plan status',
      planStatusLabel: 'Plan status',
      pmcAll: 'Any PMC rating',
      pmcLabel: 'PMC rating',
      programsAll: 'All employees',
      programsLabel: 'Development programs',
      programsOnly: 'Participants only',
      regionsAll: 'All regions',
      regionsLabel: 'Region',
      risk: {
        all: 'All',
        [TeamEmployeeRiskLevelDto.High]: 'High',
        [TeamEmployeeRiskLevelDto.Low]: 'Low',
        [TeamEmployeeRiskLevelDto.Medium]: 'Medium',
      },
      riskAll: 'Any risk of leaving',
      riskLabel: 'Risk of leaving',
      score: {
        [PmcScoreDto.Score1]: '1 · Significantly above',
        [PmcScoreDto.Score2]: '2 · Above',
        [PmcScoreDto.Score3]: '3 · As expected',
        [PmcScoreDto.Score4]: '4 · Below',
        [PmcScoreDto.Score5]: '5 · Significantly below',
      },
      sopAll: 'Any SOP rating',
      sopLabel: 'SOP rating',
      trainingsAll: 'Any activity',
      trainingsLabel: 'Activities',
    },
    headers: {
      department: 'Department / Location',
      employee: 'Employee',
      plan: 'Plan%',
      pmc: 'PMC',
      risk: 'Risk of leaving',
      roleTenure: 'Time in role',
      sop: 'SOP',
      status: 'Plan status',
    },
    loadMore: 'Load more',
    openProfile: 'Open profile',
    planStatus: {
      [TeamPlanStatusDto.Approved]: 'Approved',
      [TeamPlanStatusDto.Draft]: 'Draft',
      [TeamPlanStatusDto.None]: 'No plan',
      [TeamPlanStatusDto.Overdue]: 'Overdue',
      [TeamPlanStatusDto.Waiting]: 'Waiting',
    },
    programsCount: 'In development programs: {{inPrograms}} of {{total}}',
    roleTenureValue_one: '{{count}} year',
    roleTenureValue_other: '{{count}} years',
    searchPlaceholder: 'Search for an employee...',
    showingCount: 'Showing {{shown}} of {{total}} employees',
  },
  pipeline: {
    buckets: {
      from1To2Years: 'Medium horizon (1–2 years)',
      from3To5Years: 'Long horizon (3–5 years)',
      readyNow: 'Ready now (0 months)',
      upTo1Year: 'Short horizon (up to 1 year)',
    },
    title: 'Promotion readiness (pipeline)',
    total: 'Total in pipeline',
    totalValue: '{{value}} employees',
  },
  pmc: {
    average: 'Area PMC average',
    empty: 'Nobody in your area has a PMC score yet.',
    scores: {
      [PmcScoreDto.Score1]: '1 · Significantly above',
      [PmcScoreDto.Score2]: '2 · Above',
      [PmcScoreDto.Score3]: '3 · As expected',
      [PmcScoreDto.Score4]: '4 · Below',
      [PmcScoreDto.Score5]: '5 · Significantly below',
    },
    title: 'PMC score distribution',
  },
  risk: {
    chips: {
      risk: {
        [TeamEmployeeRiskLevelDto.High]: 'High',
        [TeamEmployeeRiskLevelDto.Low]: 'Low',
        [TeamEmployeeRiskLevelDto.Medium]: 'Medium',
      },
    },
    empty: 'Nobody in your area has a raised risk of leaving right now.',
    error: 'We could not load this list. Refresh the page and try again.',
    loadMore: 'Show more',
    peopleCount: '{{count}} people',
    riskTitle: 'Risk of leaving',
    showingCount: 'Showing {{shown}} of {{total}} people',
  },
  stats: {
    adminView: 'Viewing as: Admin',
    backToStart: 'Back to Start',
    everyoneView: 'Viewing: all employees',
    sectionTitle: 'Area potential and risk',
  },
  tabs: {
    dashboard: 'HRBP dashboard',
    hrChanges: 'HR changes',
    matrix: 'Team matrix',
    people: 'Employees',
    peopleWithCount: 'Employees ({{value}})',
    risk: 'Risk of leaving',
  },
};
